/**
 * AdminFingerprintPanel — iter242 (Admin-Detektor für Paid-Media-Kopien).
 *
 * Zwei Wege den Käufer eines verbreiteten Files zu identifizieren:
 *  1) Datei-Upload → Backend extrahiert LSB (Bild) oder Metadata-Comment
 *     (Video), fallback SHA-256 gegen `paid_media_fingerprints.delivery_content_hash`.
 *  2) Manuelle Eingabe einer Fingerprint-ID oder eines SHA-256-Hash.
 *
 * Beide Wege liefern denselben Match-Payload mit Buyer + Purchase.
 */
import { useState } from "react";
import { UploadCloud, Search, ShieldAlert, Loader2, ExternalLink, CopyIcon } from "lucide-react";
import api from "@/lib/api";

const rowStyle = "grid grid-cols-[130px_1fr] gap-2 text-xs";

function CopyChip({ value }) {
  const [copied, setCopied] = useState(false);
  // iter242 · Wenn kein Wert vorhanden ist, „–" rendern statt null (sonst
  // rutscht das 2-Spalten-Grid der MatchCard).
  if (!value) return <span className="text-[10px] text-[#999]">–</span>;
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard.writeText(String(value)); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="inline-flex items-center gap-1 max-w-full font-mono text-[10px] px-1.5 py-0.5 rounded bg-[#F5F5F5] hover:bg-[#EEE] truncate"
      title={String(value)}
      data-testid="admin-fp-copy-chip"
    >
      <span className="truncate">{value}</span>
      <CopyIcon size={10} className={copied ? "text-green-600" : "text-[#999]"} />
    </button>
  );
}

function MatchCard({ match }) {
  if (!match) return null;
  if (!match.match) {
    return (
      <div data-testid="admin-fp-no-match" className="p-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
        <div className="flex items-center gap-2 font-semibold">
          <ShieldAlert size={14} /> Kein Match gefunden
        </div>
        <p className="mt-1 opacity-80">
          Diese Datei stimmt weder mit einem bekannten Fingerprint noch mit einem Delivery-Hash überein.
          {match.extracted_fingerprint_id && <> Erkannte Fingerprint-ID: <b>{match.extracted_fingerprint_id}</b>.</>}
        </p>
      </div>
    );
  }
  return (
    <div data-testid="admin-fp-match" className="p-3 rounded-2xl bg-green-50 border border-green-200 space-y-2">
      <div className="flex items-center gap-2 font-semibold text-green-800 text-xs">
        <ShieldAlert size={14} /> Match ({match.method})
      </div>
      <div className={rowStyle}>
        <span className="text-[#666]">Fingerprint-ID</span>
        <CopyChip value={match.fingerprint_id} />
        <span className="text-[#666]">Purchase-ID</span>
        <CopyChip value={match.purchase_id} />
        <span className="text-[#666]">Asset-ID</span>
        <CopyChip value={match.asset_id} />
        <span className="text-[#666]">Media</span>
        <span>{match.media_type || "–"}</span>
        <span className="text-[#666]">SHA-256</span>
        <CopyChip value={match.content_sha256} />
        <span className="text-[#666]">Käufer</span>
        <span className="text-xs">
          {match.buyer?.email || "–"}{" "}
          {match.buyer?.user_id && <CopyChip value={match.buyer.user_id} />}
        </span>
        <span className="text-[#666]">Creator</span>
        <span>{match.creator?.display_name || match.creator?.handle || "–"}</span>
      </div>
      {match.purchase_id && (
        <a
          href={`/admin?section=orders_admin&purchase=${match.purchase_id}`}
          className="inline-flex items-center gap-1 text-xs text-[#0A0A0A] underline"
          data-testid="admin-fp-open-order"
        >
          Order öffnen <ExternalLink size={10} />
        </a>
      )}
    </div>
  );
}

export default function AdminFingerprintPanel() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [match, setMatch] = useState(null);
  const [err, setErr] = useState("");
  const [fpId, setFpId] = useState("");
  const [sha, setSha] = useState("");

  const scanFile = async () => {
    if (!file) return;
    setBusy(true); setErr(""); setMatch(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/paid-media/fingerprint-scan", fd);
      setMatch(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Scan fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const manualMatch = async () => {
    if (!fpId && !sha) return;
    setBusy(true); setErr(""); setMatch(null);
    try {
      const { data } = await api.post("/admin/paid-media/fingerprint-match", {
        fingerprint_id: fpId || undefined,
        content_sha256: sha || undefined,
      });
      setMatch(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Lookup fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-fingerprint-panel" className="space-y-4 p-4 bg-white rounded-2xl border border-[#E5E5E5]">
      <div>
        <p className="text-sm font-bold text-[#0A0A0A]">Fingerprint-Detektor</p>
        <p className="text-xs text-[#666]">
          Datei einer aufgetauchten Kopie hochladen — Backend extrahiert LSB (Bild) oder Metadaten (Video) und findet den Käufer.
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {/* Upload-Weg */}
        <div className="p-3 border border-[#E5E5E5] rounded-2xl space-y-2">
          <label className="text-xs font-semibold text-[#0A0A0A] flex items-center gap-1">
            <UploadCloud size={12} /> Datei prüfen
          </label>
          <input
            type="file"
            data-testid="admin-fp-file"
            accept="image/*,video/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-xs w-full"
          />
          {file && <p className="text-[10px] text-[#666]">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
          <button
            type="button"
            data-testid="admin-fp-scan-btn"
            onClick={scanFile}
            disabled={!file || busy}
            className="h-9 px-4 rounded-full bg-[#0A0A0A] text-white text-xs flex items-center gap-2 disabled:opacity-40"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            Fingerprint scannen
          </button>
        </div>

        {/* Manuelle Eingabe */}
        <div className="p-3 border border-[#E5E5E5] rounded-2xl space-y-2">
          <label className="text-xs font-semibold text-[#0A0A0A]">Manueller Lookup</label>
          <input
            type="text" placeholder="Fingerprint-ID"
            data-testid="admin-fp-manual-id"
            value={fpId} onChange={(e) => setFpId(e.target.value.trim())}
            className="w-full h-9 px-3 rounded-full border border-[#E5E5E5] text-xs font-mono"
          />
          <input
            type="text" placeholder="oder SHA-256 der Delivery"
            data-testid="admin-fp-manual-sha"
            value={sha} onChange={(e) => setSha(e.target.value.trim())}
            className="w-full h-9 px-3 rounded-full border border-[#E5E5E5] text-xs font-mono"
          />
          <button
            type="button"
            data-testid="admin-fp-manual-btn"
            onClick={manualMatch}
            disabled={(!fpId && !sha) || busy}
            className="h-9 px-4 rounded-full bg-[#0A0A0A] text-white text-xs flex items-center gap-2 disabled:opacity-40"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            Lookup
          </button>
        </div>
      </div>

      {err && <p data-testid="admin-fp-error" className="text-xs text-red-600">{typeof err === "string" ? err : JSON.stringify(err)}</p>}
      <MatchCard match={match} />
    </div>
  );
}
